import { CreateGameRoomOptions } from "@presidents/game-engine/src/game-room";
import { useLocation, useNavigate } from "@reach/router";
import { Field, Form, Formik } from "formik";
import React from "react";
import styled from "styled-components";
import useFetchCreateRoom from "../hooks/useFetchCreateRoom";
import { Button } from "./Button";
import Dialog from "./Dialog";
import { HeadingSmall, TitleHeading } from "./Heading";
import RadioOptionField, { RadioOptionFieldGroup } from "./RadioOptionField";
import RuleOptionField from "./RuleOptionField";
import Stack from "./Stack";

type FormValues = Pick<
  CreateGameRoomOptions,
  "rules" | "quick" | "gameLimit"
> & {
  roomType: "free" | "pro";
  roomRules: "default" | "custom";
};

const CreateRoomFormContainer = styled.div`
  padding: 40px;

  @media (max-width: 600px) {
    padding: 24px;
  }
`;

const DialogTitle = styled(TitleHeading)`
  font-size: 32px;
  text-align: center;
  margin-bottom: 32px;
  background: linear-gradient(135deg, #fff 0%, rgba(255, 255, 255, 0.7) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 20px rgba(255, 255, 255, 0.15);
`;

const SectionTitle = styled(HeadingSmall)`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const CreateRoomCTAs = styled.div`
  padding: 24px 0 0 0;
  text-align: center;
  display: flex;
  gap: 16px;
  justify-content: center;
`;

const OptionContainer = styled.div`
  padding: 16px;
  background: linear-gradient(
    135deg,
    rgba(89, 44, 166, 0.2),
    rgba(66, 33, 123, 0.2)
  );
  border-radius: 12px;
  margin-top: 24px;
`;

const Label = styled.label`
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    accent-color: var(--primary);
  }
`;

const RulesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

export const CreateRoomDialog: React.FC<{
  isOpen: boolean;
  onDismiss: () => void;
}> = ({ isOpen, onDismiss }) => {
  const navigate = useNavigate();
  const fetchCreateRoom = useFetchCreateRoom();
  const { href } = useLocation();
  const isLocalhost = new URL(href).hostname === "localhost";

  return (
    <Dialog isOpen={isOpen} onDismiss={onDismiss}>
      <CreateRoomFormContainer>
        <DialogTitle>Create New Room</DialogTitle>
        <Formik<FormValues>
          initialValues={{
            quick: false,
            roomType: "free",
            roomRules: "default",
            gameLimit: 10,
            rules: [
              {
                rule: "shouldSwap",
                fromGame: 2,
                enabled: true
              },
              {
                rule: "shouldSkip",
                fromGame: 2,
                enabled: true
              },
              {
                rule: "sixtyNine",
                fromGame: 3,
                enabled: true
              },
              {
                rule: "fourOfAKindRevolution",
                fromGame: 4,
                enabled: true
              },
              {
                rule: "largerSetBeatsSmallerSet",
                fromGame: 5,
                enabled: false
              },
              {
                rule: "twoWinsTrick",
                fromGame: 6,
                enabled: false
              }
            ]
          }}
          onSubmit={async (values, { setSubmitting }) => {
            const { data, error } = await fetchCreateRoom(values);
            if (data && data.roomId) {
              if (navigate) {
                navigate(`/${data.roomId}?autojoin`);
              }
            }
            if (error) {
              console.error(error);
            }
            setSubmitting(false);
          }}
        >
          {({ isSubmitting, values }) => (
            <Form>
              <Stack data-size="large">
                <div>
                  <SectionTitle>Room Type</SectionTitle>
                  <RadioOptionFieldGroup>
                    <RadioOptionField
                      type="radio"
                      name="roomType"
                      value="free"
                      label="Free"
                      description={
                        <>
                          4 Players max
                          <br />1 Hour time limit
                        </>
                      }
                    />
                    <RadioOptionField
                      type="radio"
                      name="roomType"
                      value="pro"
                      label="Pro"
                      description={
                        <>
                          <b>6</b> Players max
                          <br />
                          <b>24</b> Hour time limit
                        </>
                      }
                    />
                  </RadioOptionFieldGroup>
                </div>

                <div>
                  <SectionTitle>Game Rules</SectionTitle>
                  <RulesGrid>
                    {values.rules!.map((rule, i) => (
                      <RuleOptionField
                        key={i}
                        rule={rule}
                        type="checkbox"
                        name={`rules[${i}].enabled`}
                      />
                    ))}
                  </RulesGrid>
                </div>
              </Stack>

              {isLocalhost && (
                <OptionContainer>
                  <Label>
                    <Field type="checkbox" name="quick" id="quick" />
                    Quick Game (reduce card set)
                  </Label>
                </OptionContainer>
              )}

              <CreateRoomCTAs>
                <Button variant="outline" onClick={onDismiss}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} isFancy>
                  Create Room
                </Button>
              </CreateRoomCTAs>
            </Form>
          )}
        </Formik>
      </CreateRoomFormContainer>
    </Dialog>
  );
};
